// Chrome and Edge page translation can wrap or move React-owned text nodes.
// React 17 still holds the original parent/child relationship, so its next
// update may call removeChild/insertBefore with a node that is no longer a
// direct child and crash the whole page with a DOM NotFoundError.
//
// Keep the workaround restricted to the React root and its portals. DOM mistakes elsewhere
// retain the browser's normal exception instead of being silently hidden.

const GUARD = Symbol.for('lingoyou.browserTranslationDomGuard');

const belongsTo = (root, node) => {
  if (!node) return false;
  if (node === root || root.contains(node)) return true;

  // React 17 portals live outside #root, but their fibers still lead to the
  // same HostRoot. Check ownership so unrelated DOM keeps native behavior.
  for (let current = node; current; current = current.parentNode) {
    const fiberKey = Object.keys(current).find((key) =>
      key.startsWith('__reactFiber$')
    );
    for (let fiber = current[fiberKey]; fiber; fiber = fiber.return) {
      if (fiber.tag === 3 && fiber.stateNode?.containerInfo === root) {
        return true;
      }
    }
  }
  return false;
};

export function installBrowserTranslationDomGuard(root) {
  if (!root || typeof Node === 'undefined') return () => {};

  const prototype = Node.prototype;
  if (prototype[GUARD]) return prototype[GUARD].uninstall;

  const originalRemoveChild = prototype.removeChild;
  const originalInsertBefore = prototype.insertBefore;
  const originalAppendChild = prototype.appendChild;

  function guardedRemoveChild(child) {
    if (child?.parentNode === this || !belongsTo(root, this)) {
      return originalRemoveChild.call(this, child);
    }

    const translatedParent = child?.parentNode;
    if (belongsTo(root, translatedParent)) {
      return originalRemoveChild.call(translatedParent, child);
    }

    // A translator may have removed the old text node completely. React only
    // needs the deletion to be idempotent, so there is nothing left to do.
    if (!translatedParent) return child;

    return originalRemoveChild.call(this, child);
  }

  function guardedInsertBefore(newNode, referenceNode) {
    if (
      !referenceNode ||
      referenceNode.parentNode === this ||
      !belongsTo(root, this)
    ) {
      return originalInsertBefore.call(this, newNode, referenceNode);
    }

    // If translation wrapped the reference text, insert before its nearest
    // ancestor that is still a direct child. This preserves React's ordering.
    let translatedAnchor = referenceNode;
    while (
      translatedAnchor?.parentNode &&
      translatedAnchor.parentNode !== this &&
      belongsTo(root, translatedAnchor.parentNode)
    ) {
      translatedAnchor = translatedAnchor.parentNode;
    }

    if (translatedAnchor?.parentNode === this) {
      return originalInsertBefore.call(this, newNode, translatedAnchor);
    }

    // The translator moved or removed the reference. Appending is React's
    // safe fallback and avoids losing the update altogether.
    if (
      !referenceNode.parentNode ||
      belongsTo(root, referenceNode.parentNode)
    ) {
      return originalAppendChild.call(this, newNode);
    }

    return originalInsertBefore.call(this, newNode, referenceNode);
  }

  prototype.removeChild = guardedRemoveChild;
  prototype.insertBefore = guardedInsertBefore;

  const uninstall = () => {
    if (prototype.removeChild === guardedRemoveChild) {
      prototype.removeChild = originalRemoveChild;
    }
    if (prototype.insertBefore === guardedInsertBefore) {
      prototype.insertBefore = originalInsertBefore;
    }
    delete prototype[GUARD];
  };

  prototype[GUARD] = { uninstall };
  return uninstall;
}
